import React from 'react';
import PropTypes from 'prop-types';

import Icon from '@visa/vds-react/icon-styled';
import Tab from '@visa/vds-react/tab-styled';
import TabAction from '@visa/vds-react/tab-action-styled';

import CybsTooltip from '@cybs-ui/components/lib/tooltip';

import stylesBuilder from './component.styles';
import { useWindowSize } from '../../utils/customHooks';

const CybsTabItem = React.forwardRef(
  (
    {
      className,
      dataTest,
      disabled,
      headingLevel,
      href,
      i18n,
      iconClass,
      isSelected,
      // Prevent selected from being spread to VDS-React and use isSelected instead
      selected, // eslint-disable-line
      isTabMenu,
      tag,
      notification,
      notificationColor,
      role: roleProp,
      tooltipCustomCSS,
      tooltipId,
      tooltipDelayOn,
      tooltipDirection,
      tooltipShowOn,
      ...props
    },
    ref
  ) => {
    const [showTooltip, setShowTooltip] = React.useState(false);
    const { width: windowWidth } = useWindowSize();
    // WARNING: If anyone asks, props.shouldExpand doesn't exist. It is for
    //          internal use only. Anyone who depends on this behavior is wrong.
    //
    // eslint-disable-next-line react/prop-types
    const useTooltip = windowWidth <= 320 && isTabMenu && iconClass && !props.shouldExpand;

    function displayTooltip() { setShowTooltip(true); }
    function hideTooltip() { setShowTooltip(false); }

    React.useEffect(() => {
      if (!useTooltip) {
        setShowTooltip(false);
      }
    }, [useTooltip]);

    const callbacks = {};
    if (useTooltip && tooltipShowOn.includes('focus')) {
      callbacks.onFocus = displayTooltip;
      callbacks.onBlur = hideTooltip;
      callbacks.onKeyDown = function onKeyDown(e) {
        if ((e.key === 'Escape' || e.keyCode === 27) && showTooltip) {
          e.nativeEvent.stopImmediatePropagation();
          setShowTooltip(false);
        }
        // eslint-disable-next-line no-unused-expressions
        props?.onKeyDown?.(e);
      };
    }
    if (useTooltip && tooltipShowOn === 'focus-hover') {
      callbacks.onMouseEnter = displayTooltip;
      callbacks.onMouseLeave = hideTooltip;
    }

    if (iconClass) className += ' cybs-tabItem-hasIcon';
    if (isTabMenu) className += ' cybs-tabItem-isMenu';
    if (useTooltip) className += ' cybs-tabItem-collapsed';

    if (tag === 'a') {
      tag = <a href={href}>{i18n.label}</a>;
    } else {
      tag = <button type="button" />;
    }

    // a11y role logic
    let role;
    if (roleProp) {
      // Role is assigned e.g. TopBar
      role = roleProp;
    } else {
      role = tag === 'a' ? 'listitem' : 'tab';
    }

    const ariaLabel = i18n.ariaLabel || i18n.label || i18n.tooltipLabel;
    const { tabItemStyles } = stylesBuilder({
      isSelected,
      notification,
      notificationColor
    });

    const tab = (
      <>
        <Tab
          aria-expanded={isTabMenu ? isSelected : undefined}
          aria-haspopup={isTabMenu || undefined}
          className={className}
          customCSS={tabItemStyles}
          data-test={`${dataTest}-cybsTabItem`}
          disabled={disabled}
          ref={ref}
          role={role}
          selected={isSelected}
          tag={tag}
          aria-controls={undefined}
          aria-selected={isTabMenu ? undefined : isSelected}
          {...props}
          {...callbacks}
        >
          {iconClass && (
            <span className="tab-item-icon">
              <Icon name={iconClass} resolution="low" />
            </span>
          )}
          <span aria-hidden className="tabItem-visible-label">{i18n.label}</span>
          <span className="cybs-a11y-hidden">{ariaLabel}</span>
          {isTabMenu && (
            <TabAction>
              <Icon
                name={!isSelected ? 'arrow-expand' : 'arrow-collapse'}
                resolution="tiny"
              />
            </TabAction>
          )}
        </Tab>
        {isSelected && (
          <h2 aria-level={headingLevel} className="cybs-a11y-hidden">
            {i18n.label}
          </h2>
        )}
      </>
    );

    if (useTooltip) {
      return (
        <CybsTooltip
          id={tooltipId}
          dataTest={`${dataTest}-tabItem`}
          label={i18n.tooltipLabel || i18n.label || i18n.ariaLabel || iconClass}
          customCSS={tooltipCustomCSS}
          delayOn={tooltipDelayOn}
          direction={tooltipDirection}
          isShown={tooltipShowOn.includes('focus') ? showTooltip : undefined}
          showOn={tooltipShowOn}
        >
          <span style={{ padding: '0', margin: '0' }}>
            {tab}
          </span>
        </CybsTooltip>
      );
    }

    return tab;
  }
);

CybsTabItem.propTypes = {
  /**
   * CSS classes for this tab
   */
  className: PropTypes.string,

  /**
   * dataTest string for testing.
   */
  dataTest: PropTypes.string.isRequired,

  /**
   * If true, the tab is disabled.
   */
  disabled: PropTypes.bool,

  /**
   * ARIA heading level for the label of the selected TabItem.
   */
  headingLevel: PropTypes.number,

  /**
   * href if the tab is an anchor.
   */
  href: PropTypes.string,

  /**
   * icon to display on the left side of the label.
   */
  iconClass: PropTypes.string,

  /**
   * If true, the tab is selected.
   */
  isSelected: PropTypes.bool,

  /**
   * If true, the tab is selected.
   */
  isTabMenu: PropTypes.bool,

  /**
   * Localization dictionary.
   */
  i18n: PropTypes.shape({
    label: PropTypes.string,
    ariaLabel: PropTypes.string,
    tooltipLabel: PropTypes.string
  }).isRequired,

  /**
   *  If true, a notification circle will be displayed
   */
  notification: PropTypes.bool,

  /**
   *  If there's a notification, specifies the color
   */
  notificationColor: PropTypes.string,

  /**
   *  Aria Role as assigned from prop. Usually undefined.
   */
  role: PropTypes.string,

  /**
   * Type element for the tabs.
   */
  tag: PropTypes.oneOf(['a', 'button']),

  /**
   * Emotion styling function for the tooltip.
   */
  tooltipCustomCSS: PropTypes.func,

  /**
   * How many milliseconds to wait before showing the tooltip.
   */
  tooltipDelayOn: PropTypes.number,

  /**
   * Display orientation for the tooltip.
   */
  tooltipDirection: PropTypes.string,

  /**
   * Unique id to distinguish tooltips from each other.
   */
  tooltipId: PropTypes.string,
  /**
   * Trigger type for showing the tooltip.
   */
  tooltipShowOn: PropTypes.oneOf(
    'click',
    'right-click',
    'hover',
    'focus',
    'focus-hover'
  )
};

CybsTabItem.defaultProps = {
  className: '',
  disabled: false,
  headingLevel: 2,
  iconClass: null,
  isSelected: false,
  isTabMenu: false,
  href: '#',
  notification: false,
  notificationColor: '#B3202A',
  role: undefined,
  tag: 'button',
  tooltipCustomCSS: () => {},
  tooltipDelayOn: undefined,
  tooltipDirection: 'top',
  tooltipId: undefined,
  tooltipShowOn: 'focus-hover'
};

CybsTabItem.displayName = 'CybsTabItem';

export default CybsTabItem;
